import React, { useContext, useState } from 'react';

import { withStyles } from '@material-ui/core/styles';
import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Paper,
} from '@material-ui/core';

import { useHistory } from 'react-router-dom';

import moment from 'moment';

import { Formik } from 'formik';

import PropTypes from 'prop-types';

import BasicButton from '../../buttons/basicButton';
import SpinnerLoading from '../../feedback/spinnerLoading';

import { newProgram, updateProgram } from '../../../utils/comms';
import { complementaryColor, programTypes } from '../../../utils/constants';
import { eventTypesList, usersWithAccessList } from '../../../utils/helpers';

import { newProgramDetailsVS } from '../../../utils/formValidations';

import translations from '../../../languages/i18n';

import { SessionContext } from '../../../utils/context';

import generalStyles from '../../../styles/generalStyles';
import { customOptionsTableStyles, tableTitle, tableContainer } from '../../../styles/formElementsStyles';

const BlueSwitch = withStyles({
  switchBase: {
    color: 'default',
    '&$checked': {
      color: complementaryColor,
    },
    '&$checked + $track': {
      backgroundColor: complementaryColor,
    },
  },
  checked: {},
  track: {},
})(Switch);

export default function ProgramDetails({ detailsData, handleFeedback, type }) {
  const sessionContext = useContext(SessionContext);
  const classes = generalStyles();
  const classesTable = tableTitle();
  const classesTableContainer = tableContainer();
  const bgColors = customOptionsTableStyles();

  const [sentValues, setSentValues] = useState(detailsData);
  const [showLoading, setShowLoading] = useState(false);

  const history = useHistory();

  const goBack = () => {
    history.goBack();
  };

  const updateData = async (values, resetForm) => {
    setShowLoading(true);
    let response = null;
    const program = {
      name: values.name,
      open: values.open,
      sharedId: values.sharedId,
      type: values.type,
      version: values.version,
      versionDate: values.versionDate,
      // De moment els evenTypes i accessibleBy no els podem editar
      // Si no hi posam res, al servidor es posa null per tant hi ficam el que tenim dins l'estat (que inicialment es detailsData)
      accessibleBy: sentValues.accessibleBy,
      eventTypes: sentValues.eventTypes,
    };
    if (type === 'new') {
      response = await newProgram({ program }, sessionContext.userToken);
    } else {
      response = await updateProgram({ program }, detailsData._id, sessionContext.userToken);
    }
    setShowLoading(false);
    if (response.status === 200) { // Programa creat/modificat
      if (type === 'new') {
        resetForm(); // En cas de crear un nou programa, en mostrar la pantalla es mostrarà el formulari buit
      } else {
        setSentValues(response.data.program); // En cas de fer un edit, en refrescar la pantalla es mostrarà el formulari amb les dades enviades
      }
      handleFeedback('success', type === 'new' ? 'NewProgram' : 'UpdateProgram');
    } else if (response === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
      handleFeedback('error', 'TimeOut');
    } else if (response === 400) { // Ja hi ha un usuari amb aquest nom
      handleFeedback('error', type === 'new' ? 'NewProgramAlreadyExists' : 'UpdateProgramAlreadyExists');
    } else { // Error amb les dades o al servidor
      handleFeedback('error', type === 'new' ? 'NewProgram' : 'UpdateProgram');
    }
  };

  const renderRow = (data) => {
    const rows = [];
    const arrayItems = data.split(',');
    if (arrayItems.length > 0) {
      arrayItems.map((item, index) => {
        rows.push(
          <TableRow key={`${index}-${item}`} className={index % 2 ? bgColors.tableRowBgColor1 : bgColors.tableRowBgColor2}>
            <TableCell align='left'>{item}</TableCell>
          </TableRow>,
        );
      });
    } else {
      rows.push(
        <TableRow>
          <TableCell align='left'><Typography variant='h6' component='div'>{translations['_emptyList']}</Typography></TableCell>
        </TableRow>,
      );
    }
    return rows;
  };

  return (
    <Formik
      initialValues={type === 'new' ? {
        _id: '',
        name: '',
        open: true,
        sharedId: '',
        version: '',
        versionDate: '',
        type: programTypes[0].value, // Valor per defecte
        accessibleBy: '',
        eventTypes: '',
      } : {
        _id: sentValues._id,
        name: sentValues.name,
        // open: sentValues.open || true, // Idem
        open: sentValues.open,
        sharedId: sentValues.sharedId || '', // sharedId no és un camp obligatori a la BDD, si no arriba aquest camp de la BDD ficam a sharedId de Formik ''. Així evitam ficar-hi undefined
        version: sentValues.version || '', // Idem,
        versionDate: sentValues.versionDate || '', // Idem,
        type: sentValues.type || '', // Idem
        accessibleBy: (sentValues.accessibleBy && usersWithAccessList(sentValues.accessibleBy.clinicians, false)) || '', // Idem
        eventTypes: (sentValues.eventTypes && eventTypesList(sentValues.eventTypes, false)) || '', // Idem
      }}
      validationSchema={newProgramDetailsVS}
      onSubmit={(values, { resetForm }) => { updateData(values, resetForm); }}
    >
      {({ values, touched, errors, handleChange, handleBlur, submitForm }) => (
        <React.Fragment>
          <SpinnerLoading show={showLoading} />
          {/* <CustomSnackbar show={showFeedback} handleCloseFeedback={(show) => setShowFeedback(show)} type={feedbackType} message={(showFeedback && translations[`_${feedbackType}UpdateProgram`]) || ''} /> */}
          <Grid container direction='row' spacing={3} className={classes.addMargin}>
            <Grid item xs={6}>
              <div>
                <Grid container direction='column' spacing={3}>
                  <Grid item>
                    <TextField
                      disabled
                      fullWidth
                      label={translations['_idProgram']}
                      variant='outlined'
                      id='idUser'
                      name='idUser'
                      value={values._id}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      fullWidth
                      required
                      label={translations['_name']}
                      variant='outlined'
                      id='name'
                      name='name'
                      error={Boolean(touched.name && errors.name)}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.name && errors.name ? errors.name : ''}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      fullWidth
                      label={translations['_sharedId']}
                      variant='outlined'
                      id='sharedId'
                      name='sharedId'
                      value={values.sharedId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item>
                    <Grid container direction='row' justify='space-between'>
                      <Grid item xs={5}>
                        <FormControlLabel
                          control={(
                            <BlueSwitch
                              checked={values.open}
                              onChange={handleChange}
                              name='open'
                            />
                          )}
                          label={translations['_opened']}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel id='programType-label'>{translations['_type']}</InputLabel>
                          <Select
                            labelId='programType-label'
                            id='type'
                            name='type'
                            value={values.type}
                            onChange={handleChange}
                            label={translations['_type']}
                            onBlur={handleBlur}
                            // el valor per defecte d'aquest camp el carreg a l'initial values del Formik
                          >
                            {programTypes.map((element) => <MenuItem key={`programTypes_${element.value}`} value={element.value}>{element.description}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid container direction='row' justify='space-between'>
                      <Grid item xs={5}>
                        <TextField
                          label={translations['_version']}
                          variant='outlined'
                          id='version'
                          name='version'
                          value={values.version}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={translations['_versionDate']}
                          variant='outlined'
                          type='date'
                          id='versionDate'
                          name='versionDate'
                          value={moment(values.versionDate).format('YYYY-MM-DD')} // Per adequar el format de la data al que necessita el TextField
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{ max: new Date().toISOString().split('T')[0] }} // Per evitar que es pugui introduir una data posterior al dia actual
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </Grid>
            {type !== 'new' && (
              <Grid item xs={6}>
                <TableContainer component={Paper} className={classesTableContainer.container385px}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={bgColors.tableRowHeaderBgColor}>
                          <Typography component='div' className={classesTable.titleWithOutMargin}>
                            {translations['_eventTypes']}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {renderRow(values.eventTypes)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>

          {type !== 'new' && (
            <Grid container direction='row' justify='center' spacing={3} className={classes.addMargin}>
              <Grid item xs={6}>
                <TableContainer component={Paper} className={classesTableContainer.container385px}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={bgColors.tableRowHeaderBgColor}>
                          <Typography component='div' className={classesTable.titleWithOutMargin}>
                            {translations['_accessibleBy']}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {renderRow(values.accessibleBy)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={6}></Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={type === 'new' ? 6 : 12}>
                <Grid container spacing={2} direction='row' justify={type === 'new' ? 'flex-end' : 'center'}>
                  <Grid item>
                    {sessionContext.role === 'Admin' && <BasicButton handleClick={submitForm} type='accept' />}
                  </Grid>
                  <Grid item>
                    <BasicButton handleClick={goBack} type='cancel' />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </Formik>
  );
}

// Indica i controla els props que se han de passar al component
ProgramDetails.propTypes = {
  detailsData: PropTypes.object.isRequired,
  handleFeedback: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};
